<script setup lang="ts">
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const props = withDefaults(defineProps<{
    presentation?: 'standard' | 'contained' | 'immersive' | 'immersive-extended';
    backgroundMode?: 'inline' | 'frame';
    audience?: 'staff' | 'portal';
    backgroundImageUrl?: string;
    backgroundAlt?: string;
    backgroundFallbackStyle?: Record<string, string>;
    avatarImageUrl?: string;
    avatarAlt?: string;
    avatarFallback: string;
    avatarPlacement?: 'inline' | 'above';
    title: string;
}>(), {
    presentation: 'standard',
    backgroundMode: 'inline',
    audience: 'staff',
    backgroundImageUrl: '',
    backgroundAlt: '',
    backgroundFallbackStyle: () => ({}),
    avatarImageUrl: '',
    avatarAlt: '',
    avatarPlacement: 'inline',
});

const isImmersive = computed(() => props.presentation === 'immersive' || props.presentation === 'immersive-extended');
const isExtendedImmersive = computed(() => props.presentation === 'immersive-extended');
const isContained = computed(() => props.presentation === 'contained');
const isPortal = computed(() => props.audience === 'portal');
const isAvatarAbove = computed(() => props.avatarPlacement === 'above');
const mediaHeightClass = computed(() => {
    if (!isImmersive.value) return 'min-h-70 sm:min-h-80';
    if (isPortal.value) return 'min-h-[27rem] sm:min-h-[29rem] lg:min-h-[32rem]';
    if (isExtendedImmersive.value) return 'min-h-[36rem] sm:min-h-[40rem] lg:min-h-[44rem]';
    return 'min-h-[34rem] sm:min-h-[36rem] lg:min-h-[40rem]';
});
const contentPositionClass = computed(() => {
    if (!isImmersive.value) return 'bottom-0 p-5 sm:p-6';
    if (isPortal.value) return 'bottom-8 p-6 sm:bottom-9 sm:p-9 lg:bottom-10 lg:p-12';
    if (isExtendedImmersive.value) return 'bottom-28 p-6 sm:bottom-32 sm:p-9 lg:bottom-36 lg:p-12';
    return 'bottom-20 p-6 sm:bottom-24 sm:p-9 lg:bottom-28 lg:p-12';
});
</script>

<template>
    <section
        class="group/hero"
        :data-audience="props.audience"
        :data-presentation="props.presentation"
        :class="[
            isImmersive
                ? 'workspace-hero-immersive overflow-visible border-0 bg-transparent shadow-none'
                : 'overflow-hidden border border-border/60 bg-card shadow-none',
            isExtendedImmersive ? 'workspace-hero-immersive-extended' : '',
            isContained ? 'workspace-hero-contained rounded-2xl' : (!isImmersive ? 'rounded-xl' : ''),
        ]"
    >
        <div
            class="relative"
            :class="mediaHeightClass"
        >
            <div
                v-if="props.backgroundMode === 'inline'"
                class="absolute inset-0 overflow-hidden"
                :class="isImmersive ? 'workspace-hero-media-immersive' : ''"
            >
                <img
                    v-if="backgroundImageUrl"
                    :src="backgroundImageUrl"
                    :alt="backgroundAlt || `${title} header image`"
                    class="absolute inset-0 size-full object-cover"
                    :class="isImmersive ? 'scale-[1.015] transition-transform duration-700 ease-out group-hover/hero:scale-[1.035] motion-reduce:transform-none motion-reduce:transition-none' : ''"
                >
                <div
                    v-else
                    class="absolute inset-0 bg-muted"
                    :style="backgroundFallbackStyle"
                />

                <div
                    class="absolute inset-0"
                    :class="isImmersive
                        ? 'workspace-hero-overlay-immersive'
                        : 'workspace-hero-overlay-standard'"
                />
                <div
                    v-if="isImmersive"
                    class="workspace-hero-vignette-immersive absolute inset-0"
                />
            </div>

            <div
                class="absolute z-10 flex flex-col items-end gap-2"
                :class="isImmersive ? 'right-5 top-5 sm:right-8 sm:top-8' : 'right-4 top-4'"
            >
                <div
                    v-if="$slots['background-action']"
                    class="opacity-100 transition sm:pointer-events-none sm:opacity-0 sm:group-hover/hero:pointer-events-auto sm:group-hover/hero:opacity-100 sm:group-focus-within/hero:pointer-events-auto sm:group-focus-within/hero:opacity-100"
                >
                    <slot name="background-action" />
                </div>

                <div v-if="$slots['top-action']">
                    <slot name="top-action" />
                </div>
            </div>

            <div
                class="absolute inset-x-0 z-1"
                :class="contentPositionClass"
            >
                <div
                    class="flex flex-col lg:flex-row lg:items-end lg:justify-between"
                    :class="isImmersive ? 'gap-7 lg:gap-10' : 'gap-5'"
                >
                    <div
                        class="flex min-w-0 flex-1 items-start"
                        :class="[
                            isImmersive ? 'gap-4 sm:gap-6' : 'gap-4',
                            isAvatarAbove ? 'flex-col' : (isImmersive ? 'flex-col sm:flex-row' : 'flex-row'),
                        ]"
                    >
                        <div class="group/avatar relative shrink-0">
                            <Avatar
                                class="border border-white/20 dark:border-white/15"
                                :class="isAvatarAbove
                                    ? 'h-14 w-14 sm:h-16 sm:w-16'
                                    : (isImmersive ? 'h-20 w-20 sm:h-28 sm:w-28' : 'h-20 w-20')"
                            >
                                <AvatarImage :src="avatarImageUrl || ''" :alt="avatarAlt || title" />
                                <AvatarFallback class="bg-primary text-lg font-semibold text-primary-foreground">
                                    {{ avatarFallback }}
                                </AvatarFallback>
                            </Avatar>

                            <div
                                v-if="$slots['avatar-action']"
                                class="absolute -bottom-1 -right-1 opacity-100 transition sm:pointer-events-none sm:opacity-0 sm:group-hover/avatar:pointer-events-auto sm:group-hover/avatar:opacity-100 sm:group-focus-within/avatar:pointer-events-auto sm:group-focus-within/avatar:opacity-100"
                            >
                                <slot name="avatar-action" />
                            </div>
                        </div>

                        <div
                            class="min-w-0"
                            :class="isAvatarAbove
                                ? 'self-stretch space-y-4'
                                : (isImmersive ? 'self-stretch space-y-4 sm:self-end' : 'self-end space-y-3')"
                        >
                            <slot name="title">
                                <h1
                                    class="font-display font-semibold tracking-tight"
                                    :class="[
                                        isImmersive ? 'text-foreground' : 'text-white',
                                        isImmersive ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl',
                                    ]"
                                >
                                    {{ title }}
                                </h1>
                            </slot>

                            <div class="flex flex-wrap items-center gap-2">
                                <slot name="badges" />
                            </div>

                            <div
                                class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm"
                                :class="isImmersive ? 'text-foreground/78' : 'text-white/85'"
                            >
                                <slot name="meta" />
                            </div>

                            <div v-if="$slots.supplemental" class="flex flex-wrap items-center gap-2 pt-1">
                                <slot name="supplemental" />
                            </div>
                        </div>
                    </div>

                    <div v-if="$slots.actions" class="flex w-full shrink-0 flex-wrap items-center gap-2 md:w-auto lg:justify-end">
                        <slot name="actions" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
